export const CATEGORY_COLOR_KEYS = [
    'rose',
    'orange',
    'yellow',
    'lime',
    'teal',
    'sky',
    'indigo',
    'fuchsia',
    'slate',
] as const;

export type CategoryColorKey = (typeof CATEGORY_COLOR_KEYS)[number];

export interface Category {
    name: string;
    color: CategoryColorKey;
}

export interface CategoryUsage extends Category {
    count: number;
}

export const categoryKey = (category: Category) =>
    `${category.name}|${category.color}`;

interface CategoryColorClasses {
    swatch: string;
    icon: string;
    iconBg: string;
    chipBg: string;
    chipText: string;
    chipBorder: string;
    dot: string;
}

export const CATEGORY_COLORS: Record<CategoryColorKey, CategoryColorClasses> = {
    rose: {
        swatch: 'bg-rose-500',
        icon: 'text-rose-100',
        iconBg: 'bg-rose-950',
        chipBg: 'bg-rose-950',
        chipText: 'text-rose-100',
        chipBorder: 'border-rose-800',
        dot: 'bg-rose-100',
    },
    orange: {
        swatch: 'bg-orange-500',
        icon: 'text-orange-100',
        iconBg: 'bg-orange-950',
        chipBg: 'bg-orange-950',
        chipText: 'text-orange-100',
        chipBorder: 'border-orange-800',
        dot: 'bg-orange-100',
    },
    yellow: {
        swatch: 'bg-yellow-500',
        icon: 'text-yellow-100',
        iconBg: 'bg-yellow-950',
        chipBg: 'bg-yellow-950',
        chipText: 'text-yellow-100',
        chipBorder: 'border-yellow-800',
        dot: 'bg-yellow-100',
    },
    lime: {
        swatch: 'bg-lime-500',
        icon: 'text-lime-100',
        iconBg: 'bg-lime-950',
        chipBg: 'bg-lime-950',
        chipText: 'text-lime-100',
        chipBorder: 'border-lime-800',
        dot: 'bg-lime-100',
    },
    teal: {
        swatch: 'bg-teal-500',
        icon: 'text-teal-100',
        iconBg: 'bg-teal-950',
        chipBg: 'bg-teal-950',
        chipText: 'text-teal-100',
        chipBorder: 'border-teal-800',
        dot: 'bg-teal-100',
    },
    sky: {
        swatch: 'bg-sky-500',
        icon: 'text-sky-100',
        iconBg: 'bg-sky-950',
        chipBg: 'bg-sky-950',
        chipText: 'text-sky-100',
        chipBorder: 'border-sky-800',
        dot: 'bg-sky-100',
    },
    indigo: {
        swatch: 'bg-indigo-500',
        icon: 'text-indigo-100',
        iconBg: 'bg-indigo-950',
        chipBg: 'bg-indigo-950',
        chipText: 'text-indigo-100',
        chipBorder: 'border-indigo-800',
        dot: 'bg-indigo-100',
    },
    fuchsia: {
        swatch: 'bg-fuchsia-500',
        icon: 'text-fuchsia-100',
        iconBg: 'bg-fuchsia-950',
        chipBg: 'bg-fuchsia-950',
        chipText: 'text-fuchsia-100',
        chipBorder: 'border-fuchsia-800',
        dot: 'bg-fuchsia-100',
    },
    slate: {
        swatch: 'bg-slate-500',
        icon: 'text-slate-100',
        iconBg: 'bg-slate-800',
        chipBg: 'bg-slate-800',
        chipText: 'text-slate-100',
        chipBorder: 'border-slate-600',
        dot: 'bg-slate-100',
    },
};

export const isCategoryColorKey = (value: unknown): value is CategoryColorKey =>
    typeof value === 'string' &&
    (CATEGORY_COLOR_KEYS as readonly string[]).includes(value);

export const getCategoryColorClasses = (color: string): CategoryColorClasses =>
    isCategoryColorKey(color) ? CATEGORY_COLORS[color] : CATEGORY_COLORS.slate;
